// Beat Detective wie in Pro Tools (Reference Guide, Kapitel Beat Detective):
//  1. Erkennen: Transienten (Anschläge) in der Auswahl finden, über alle gewählten Spuren gemeinsam (Collection Mode),
//     damit Mehrspur-Drums phasengleich bleiben. Empfindlichkeit und Auflösung (Takte, Schläge, Unterteilung) filtern.
//  2. Clip Separation: alle Spuren an den Triggern teilen (mit Trigger Pad vor dem Anschlag).
//  3. Clip Conform: die Stücke aufs Raster ziehen, mit Stärke, Ausschluss (was schon nah dran ist, bleibt) und Swing.
//  4. Edit Smoothing: Lücken füllen und optional kurze Crossfades vor jedem Anschlag.
// Reine Funktionen: Erkennung auf Sample-Daten, Bearbeitung auf dem Projekt (innerhalb von commit()).
import type { Project } from '@srs/shared';
import { clipMap } from './warp.ts';
import { EPS, clipEnd, splitTracksAt, setBounds, trimLimits, type Range, type SrcLen } from './ops.ts';
import { GRID_BEATS, barAtSec, barToBeat, beatAt, beatToBar, bpmAt, meterAt, meterAtBar, secAt, spbAt } from '@srs/shared';

export type Emphasis = 'high' | 'low' | 'enhanced';
export type Resolution = 'bar' | 'beat' | 'sub';
export interface BeatOpts {
  /** Empfindlichkeit 0–100 %: mehr findet auch leisere Anschläge */
  sensitivity: number;
  resolution: Resolution;
  /** Unterteilung für die Auflösung „Unterteilung“ und fürs Conform (Raster-Id wie 1/16) */
  sub: string;
  /** Analyse: hohe Frequenzen betonen (Hi-Hat, Snare), tiefe (Kick, Bass) oder beides */
  emphasis: Emphasis;
  /** Trigger Pad: so viel vor dem Anschlag wird geschnitten (ms), damit die Attacke ganz bleibt */
  padMs: number;
  /** Conform: Stärke 0–100 % */
  strength: number;
  /** Conform: was schon innerhalb dieses Anteils (0–100 % des halben Rasters) liegt, bleibt unverändert */
  excludeWithin: number;
  /** Conform: Swing 0–150 % (100 % = Triolen-Feel) auf jede zweite Unterteilung */
  swing: number;
  /** Edit Smoothing: nur Lücken füllen oder zusätzlich Crossfades */
  smooth: 'fill' | 'xfade';
  /** Crossfade-Länge in ms (endet vor dem Anschlag) */
  xfadeMs: number;
}
export const DEFAULT_BEAT: BeatOpts = { sensitivity: 60, resolution: 'sub', sub: '1/16', emphasis: 'enhanced', padMs: 5, strength: 100, excludeWithin: 0, swing: 0, smooth: 'xfade', xfadeMs: 5 };

/** Erkannter Anschlag in Timeline-Zeit */
export interface Onset { t: number; s: number }
export interface Trigger extends Onset { cls: Resolution; grid: number }

// ---------------------------------------------------------------------------
// Erkennung

/** Anschläge in einem Ausschnitt der Quelle. Zeiten in Sekunden relativ zum Ausschnittsanfang, Stärke 0..1 (roh). */
export function detectOnsets(channels: Float32Array[], sr: number, from: number, to: number, emphasis: Emphasis): Onset[] {
  const len = channels[0]?.length ?? 0;
  const i0 = Math.max(0, Math.floor(from * sr)), i1 = Math.min(len, Math.ceil(to * sr)), n = i1 - i0;
  if (n < 256) return [];
  // Mono-Summe
  const x = new Float32Array(n);
  for (const ch of channels) for (let i = 0; i < n; i++) x[i] += ch[i0 + i] / channels.length;
  // Bänder: hoch (Differenz), tief (einpoliger Tiefpass ~ 180 Hz)
  const hi = new Float32Array(n), lo = new Float32Array(n);
  const a = Math.exp(-2 * Math.PI * 180 / sr);
  let prev = 0, lp = 0;
  for (let i = 0; i < n; i++) { hi[i] = x[i] - 0.95 * prev; prev = x[i]; lp = (1 - a) * x[i] + a * lp; lo[i] = lp; }
  const bands = emphasis === 'high' ? [hi] : emphasis === 'low' ? [lo] : [hi, lo, x];
  const hop = Math.max(32, Math.round(sr / 375)), win = hop * 4;   // ~2,7 ms Schritt, ~10,7 ms Fenster bei 48 kHz
  const frames = Math.floor((n - win) / hop) + 1;
  if (frames < 8) return [];
  // Anstieg in dB gegenüber dem Mittel der letzten ~16 ms, je Band, summiert
  const rise = new Float32Array(frames), level = new Float32Array(frames);
  for (const b of bands) {
    const L = new Float32Array(frames);
    for (let f = 0; f < frames; f++) {
      let e = 0;
      for (let i = f * hop, k = i + win; i < k; i++) e += b[i] * b[i];
      L[f] = 10 * Math.log10(e / win + 1e-12);
    }
    for (let f = 0; f < frames; f++) {
      let m = 0, c = 0;
      for (let k = Math.max(0, f - 6); k < f; k++) { m += L[k]; c++; }
      const d = c ? L[f] - m / c : 0;
      if (d > 0 && L[f] > -66) rise[f] += d / bands.length;
      level[f] = Math.max(level[f], L[f]);
    }
  }
  // Spitzen: lokales Maximum über ±20 ms, Mindestanstieg 2 dB
  const R = Math.max(2, Math.round(0.02 * sr / hop)), raw: Onset[] = [];
  for (let f = 1; f < frames; f++) {
    const v = rise[f];
    if (v < 2) continue;
    let peak = true;
    for (let k = Math.max(0, f - R); k <= Math.min(frames - 1, f + R) && peak; k++) if (rise[k] > v || (rise[k] === v && k < f)) peak = false;
    if (!peak) continue;
    // Genaue Lage: Beginn der Attacke im Bereich des Fensters (erste Stelle über 20 % der Spitze davor)
    const s0 = f * hop, s1 = Math.min(n, s0 + win + hop * 2);
    let pk = 0, pi = s0;
    for (let i = s0; i < s1; i++) { const v2 = Math.abs(x[i]); if (v2 > pk) { pk = v2; pi = i; } }
    let at = pi;
    while (at > Math.max(0, s0 - hop * 2) && Math.abs(x[at - 1]) > pk * 0.2) at--;
    // bis zur letzten ruhigen Stelle davor (Nulldurchgang in Ruhe)
    let q = at;
    for (let i = at; i > Math.max(0, at - Math.round(0.004 * sr)); i--) { if (Math.abs(x[i]) < pk * 0.05) { q = i; break; } }
    const lvl = Math.max(0, Math.min(1, (level[Math.min(frames - 1, f + 2)] + 60) / 54));
    raw.push({ t: (i0 + q) / sr - from, s: Math.min(1, v / 24) * (0.35 + 0.65 * lvl) });
  }
  return dedupe(raw, 0.03);
}

/** Anschläge, die näher als gap beieinander liegen, zusammenfassen: der frühere bleibt (phasengleich), mit der größeren Stärke */
export function dedupe(list: Onset[], gap: number): Onset[] {
  const s = [...list].sort((a, b) => a.t - b.t), out: Onset[] = [];
  for (const o of s) {
    const last = out[out.length - 1];
    if (last && o.t - last.t < gap) last.s = Math.max(last.s, o.s);
    else out.push({ ...o });
  }
  return out;
}

/** Anschläge aller Audio-Clips in der Auswahl (Timeline-Zeiten), über alle Spuren zusammengeführt */
export function detectRange(p: Project, r: Range, buffers: (id: string) => AudioBuffer | undefined, emphasis: Emphasis): { onsets: Onset[]; clips: number; missing: number } {
  const all: Onset[] = [];
  let clips = 0, missing = 0;
  for (const c of p.clips) {
    if (c.kind !== 'audio' || c.muted || !r.trackIds.includes(c.trackId) || c.start >= r.end || clipEnd(c) <= r.start) continue;
    const b = c.sourceId ? buffers(c.sourceId) : undefined;
    if (!b) { missing++; continue; }
    if (c.loop) continue;
    clips++;
    const a = Math.max(r.start, c.start), e = Math.min(r.end, clipEnd(c));
    if (c.warp) {
      // Elastic-Clip: in der Quelle suchen, über die Warp-Zuordnung auf die Timeline legen
      const m = clipMap(p, c), sa = m.toSrc(a) - 0.03, se = m.toSrc(e);
      const chansW = Array.from({ length: b.numberOfChannels }, (_, i) => b.getChannelData(i));
      const gW = Math.pow(10, (c.gainDb ?? 0) / 20);
      for (const o of detectOnsets(chansW, b.sampleRate, Math.max(0, sa), se, emphasis)) {
        const t = m.toTime(Math.max(0, sa) + o.t);
        if (t >= r.start - 0.002 && t < r.end) all.push({ t: Math.max(r.start, t), s: o.s * Math.min(1, gW) });
      }
      continue;
    }
    // etwas Vorlauf, damit der Anstieg am Anfang erkannt wird
    const pre = Math.min(0.03, a - c.start + c.offset);
    const chans = Array.from({ length: b.numberOfChannels }, (_, i) => b.getChannelData(i));
    const g = Math.pow(10, (c.gainDb ?? 0) / 20);
    for (const o of detectOnsets(chans, b.sampleRate, c.offset + (a - c.start) - pre, c.offset + (e - c.start), emphasis)) {
      const t = a - pre + o.t;
      if (t >= r.start - 0.002 && t < r.end) all.push({ t: Math.max(r.start, t), s: o.s * Math.min(1, g) });
    }
  }
  const merged = dedupe(all, 0.025), max = Math.max(1e-9, ...merged.map((o) => o.s));
  return { onsets: merged.map((o) => ({ t: o.t, s: o.s / max })), clips, missing };
}

/** Schrittweite der Auflösung in Vierteln an einem Takt */
function stepBeats(p: Project, bar: number, o: Pick<BeatOpts, 'resolution' | 'sub'>) {
  const m = meterAtBar(p, bar);
  return o.resolution === 'bar' ? m.barLen : o.resolution === 'beat' ? 4 / m.den : (GRID_BEATS[o.sub] ?? 0.25);
}
/** Nächster Rasterpunkt (Viertel) zu einer Viertel-Position, Unterteilungen beginnen in jedem Takt neu */
function nearestGrid(p: Project, b: number, o: Pick<BeatOpts, 'resolution' | 'sub'>): { gb: number; bar: number; step: number } {
  const bar = Math.floor(beatToBar(p, b) + 1e-9), b0 = barToBeat(p, bar), len = meterAtBar(p, bar).barLen, step = stepBeats(p, bar, o);
  let gb = b0 + Math.round((b - b0) / step) * step;
  if (gb > b0 + len - 1e-9) gb = b0 + len;
  return { gb, bar, step };
}

/** Empfindlichkeit und Auflösung anwenden: je Rasterpunkt der stärkste Anschlag in seiner Nähe (folgt Tempo- und Taktwechseln) */
export function classify(p: Project, onsets: Onset[], o: Pick<BeatOpts, 'sensitivity' | 'resolution' | 'sub'>): Trigger[] {
  const min = 1 - o.sensitivity / 100;
  const best = new Map<number, Trigger>();
  for (const x of onsets) {
    if (x.s < min - 1e-9) continue;
    const b = beatAt(p, x.t), { gb, bar, step } = nearestGrid(p, b, o);
    // Nähe zum Rasterpunkt: halbe Unterteilung, bei Takten und Schlägen eine Sechzehntel
    const tol = o.resolution === 'sub' ? step * 0.5 : (4 / meterAtBar(p, bar).den) * 0.25;
    if (Math.abs(b - gb) >= tol - 1e-9) continue;
    const g = secAt(p, gb), xb = beatToBar(p, gb), bs = barToBeat(p, Math.round(xb)), unit = 4 / meterAtBar(p, Math.floor(xb + 1e-9)).den;
    const cls: Resolution = Math.abs(xb - Math.round(xb)) < 1e-6 ? 'bar' : Math.abs((gb - bs) / unit - Math.round((gb - bs) / unit)) < 1e-6 ? 'beat' : 'sub';
    const k = Math.round(gb * 1e4), cur = best.get(k);
    if (!cur || x.s > cur.s + 1e-9 || (Math.abs(x.s - cur.s) < 1e-9 && Math.abs(x.t - g) < Math.abs(cur.t - g))) best.set(k, { ...x, cls, grid: g });
  }
  return [...best.values()].sort((a, b) => a.t - b.t);
}

/** Zielzeit eines Triggers beim Conform (Raster, Swing, Stärke, Ausschluss) */
export function conformTarget(p: Project, tr: Trigger, o: Pick<BeatOpts, 'resolution' | 'sub' | 'strength' | 'excludeWithin' | 'swing'>): number {
  const gb = beatAt(p, tr.grid), bar = Math.floor(beatToBar(p, gb) + 1e-9), step = stepBeats(p, bar, o);
  let tb = gb;
  // Swing: jede zweite Unterteilung im Takt nach hinten (100 % = Triole)
  if (o.swing > 0 && o.resolution === 'sub' && Math.round((gb - barToBeat(p, bar)) / step) % 2 !== 0) tb += (o.swing / 100) * (step / 3);
  const target = secAt(p, tb), dev = target - tr.t, stepSec = step * spbAt(p, tr.grid);
  if (o.excludeWithin > 0 && Math.abs(dev) <= (o.excludeWithin / 100) * (stepSec / 2) + 1e-9) return tr.t;
  return tr.t + dev * (o.strength / 100);
}

// ---------------------------------------------------------------------------
// Bearbeitung

const pad = (o: Pick<BeatOpts, 'padMs'>) => Math.max(0, o.padMs) / 1000;

/** Clip Separation: alle gewählten Spuren an jedem Trigger teilen (Trigger Pad davor). Gibt die Zahl der Schnitte zurück. */
export function beatSeparate(p: Project, r: Range, triggers: Trigger[], o: Pick<BeatOpts, 'padMs'>): number {
  let n = 0;
  for (const tr of triggers) {
    const at = tr.t - pad(o);
    if (at <= r.start + EPS || at >= r.end - EPS) continue;
    const before = p.clips.length;
    splitTracksAt(p, r.trackIds, at);
    if (p.clips.length > before) n++;
  }
  return n;
}

/** Clip Conform: getrennte Stücke so verschieben, dass ihr Anschlag auf dem Raster liegt. Überlappungen werden gekürzt. */
export function beatConform(p: Project, r: Range, triggers: Trigger[], o: BeatOpts): number {
  beatSeparate(p, r, triggers, o);
  const P = pad(o);
  // Zielzeiten berechnen und Reihenfolge erzwingen (mindestens 10 ms Abstand)
  const moves: { at: number; delta: number }[] = [];
  let last = -Infinity;
  for (const tr of triggers) {
    const at = tr.t - P;
    if (at < r.start - EPS || at >= r.end - EPS) continue;
    let nt = conformTarget(p, tr, o);
    if (nt - P < last + 0.01) nt = last + 0.01 + P;
    last = nt - P;
    moves.push({ at, delta: nt - tr.t });
  }
  let moved = 0;
  for (const tid of r.trackIds) {
    const hits = moves.filter((m) => Math.abs(m.delta) > 1e-6).flatMap((m) => p.clips.filter((c) => c.trackId === tid && c.kind === 'audio' && Math.abs(c.start - m.at) < 0.0005).map((c) => [c, m.delta] as const));
    for (const [c, d] of hits) { c.start = Math.max(0, c.start + d); moved++; }
    // Überlappungen auflösen: vorheriges Stück endet am nächsten Anschlag
    const list = p.clips.filter((c) => c.trackId === tid && c.kind === 'audio' && clipEnd(c) > r.start - 1 && c.start < r.end + 1).sort((a, b) => a.start - b.start);
    for (let i = 0; i < list.length - 1; i++) {
      const a = list[i], b = list[i + 1];
      if (clipEnd(a) > b.start + EPS && b.start > a.start + EPS) { a.duration = b.start - a.start; a.fadeOut = Math.min(a.fadeOut, a.duration); a.fadeIn = Math.min(a.fadeIn, a.duration - a.fadeOut); }
    }
  }
  return moved;
}

/** Edit Smoothing: Lücken zwischen den Stücken füllen (Enden ausziehen) und optional Crossfades vor jedem Anschlag */
export function beatSmooth(p: Project, r: Range, o: Pick<BeatOpts, 'smooth' | 'xfadeMs'>, srcLen: SrcLen): { filled: number; crossfades: number } {
  const out = { filled: 0, crossfades: 0 };
  const X = o.smooth === 'xfade' ? Math.max(0, o.xfadeMs) / 1000 : 0;
  for (const tid of r.trackIds) {
    const list = p.clips.filter((c) => c.trackId === tid && c.kind === 'audio' && clipEnd(c) > r.start - EPS && c.start < r.end + EPS).sort((a, b) => a.start - b.start);
    for (let i = 0; i < list.length - 1; i++) {
      const a = list[i], b = list[i + 1];
      if (b.start - clipEnd(a) > EPS) {
        // Lücke zuerst mit dem Material vor dem Anschlag von b füllen (Ausklang davor), nicht mit dem Ende von a:
        // Das Ende von a enthält sonst den ursprünglichen Anschlag von b an der alten Stelle (Doppelanschlag, Flam).
        const lb = trimLimits(b, srcLen, p), ns = Math.max(clipEnd(a), lb.minStart);
        if (ns < b.start - EPS) setBounds(p, b, ns, clipEnd(b));
        if (b.start - clipEnd(a) > EPS) { const lim = trimLimits(a, srcLen, p).maxEnd; a.duration = Math.max(a.duration, Math.min(b.start, lim) - a.start); }
        out.filled++;
      }
      if (X > 0 && Math.abs(clipEnd(a) - b.start) < 0.0005 && b.start > r.start + EPS && b.start < r.end - EPS) {
        // Crossfade endet am Schnitt: der Anschlag von b bleibt unberührt
        const lb = trimLimits(b, srcLen, p), x = Math.min(X, b.start - lb.minStart, a.duration * 0.5);
        if (x > 0.0002) {
          const seam = b.start;
          setBounds(p, b, seam - x, clipEnd(b));
          b.fadeIn = x; b.fadeInShape = 'equalPower';
          a.fadeOut = x; a.fadeOutShape = 'equalPower';
          a.fadeOut = Math.min(a.fadeOut, a.duration);
          out.crossfades++;
        }
      }
    }
  }
  return out;
}

/** Tempo aus einer Auswahl, die genau n Takte umfasst (Taktart am Anfang der Auswahl) */
export function tempoFromSelection(p: Project, r: { start: number; end: number }, bars: number): number {
  const sec = r.end - r.start;
  if (sec <= 0 || bars <= 0) return bpmAt(p, r.start);
  return Math.round((bars * meterAt(p, r.start).barLen * 60 / sec) * 100) / 100;
}

/**
 * Bar|Beat-Marker aus einer frei eingespielten Aufnahme (Pro Tools: Bar|Beat Marker Generation).
 * Die Auswahl beginnt auf einer Eins und umfasst genau `bars` Takte. Für jeden Takt (oder Schlag) wird der passende
 * Anschlag gesucht, dazwischen ein Tempowechsel gesetzt: Das Raster folgt danach dem Spiel, die Aufnahme bleibt unverändert.
 * Gibt die Tempowechsel zurück (Viertel-Position, BPM) und wo Takt `bar0` (0-basiert) beginnt.
 */
export function tempoFromPerformance(p: Project, onsets: Onset[], r: { start: number; end: number }, bars: number, per: 'bar' | 'beat', sensitivity: number): { bar0: number; points: number[]; events: { beat: number; bpm: number }[]; leadIn?: number } | null {
  if (bars < 1 || r.end - r.start < 0.2) return null;
  const strong = onsets.filter((o) => o.s >= 1 - sensitivity / 100 - 1e-9);
  // Takt, auf dem die Auswahl beginnt (nächste Taktlinie im jetzigen Raster); vor Takt 2 braucht es einen Vorlauftakt
  const nearest = (t: number, tol: number) => { let bestO: Onset | null = null; for (const o of strong) if (Math.abs(o.t - t) <= tol && (!bestO || o.s > bestO.s + 1e-9 || (Math.abs(o.s - bestO.s) < 1e-9 && Math.abs(o.t - t) < Math.abs(bestO.t - t)))) bestO = o; return bestO; };
  const m = meterAtBar(p, Math.max(0, Math.round(barAtSec(p, r.start)))), unitBeats = per === 'bar' ? m.barLen : 4 / m.den;
  const count = per === 'bar' ? bars : bars * m.num;
  const avg0 = (r.end - r.start) / count;
  const t0 = nearest(r.start, avg0 * 0.25)?.t ?? r.start;
  // Punkte von Schlag zu Schlag verfolgen: Erwartung aus dem letzten Abstand, stärkster Anschlag in ±30 %
  const pts = [t0];
  let iv = (r.end - t0) / count;
  for (let i = 1; i <= count; i++) {
    const remain = count - i + 1, expect = i === count ? r.end : pts[i - 1] + iv;
    const hit = nearest(expect, iv * 0.3);
    let t = hit ? hit.t : expect;
    if (i === count) t = nearest(r.end, iv * 0.3)?.t ?? r.end;
    if (t <= pts[i - 1] + iv * 0.4) t = pts[i - 1] + iv;
    pts.push(t);
    const local = t - pts[i - 1], rest = (r.end - t) / Math.max(1, remain - 1);
    iv = remain > 1 ? 0.6 * local + 0.4 * rest : local;
  }
  let bar0 = Math.max(0, Math.round(barAtSec(p, t0)));
  if (bar0 === 0 && t0 > 0.02) bar0 = 1;
  const b0 = barToBeat(p, bar0), events: { beat: number; bpm: number }[] = [];
  let leadIn: number | undefined;
  // Vorlauf: Tempo bis zur ersten Eins so, dass Takt bar0 genau auf dem ersten Anschlag beginnt
  if (bar0 > 0) { leadIn = Math.round((b0 * 60 / t0) * 100) / 100; events.push({ beat: 0, bpm: leadIn }); }
  for (let i = 0; i < count; i++) events.push({ beat: b0 + i * unitBeats, bpm: (unitBeats * 60) / (pts[i + 1] - pts[i]) });
  return { bar0, points: pts, events, leadIn };
}
